import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Save, Upload, Globe2, FileText, Download, Sparkles, Trash2, Plus, ArrowLeft, Loader2, Presentation, MessageCircle, X } from "lucide-react";
import { api, API_BASE } from "../lib/api";
import { toast } from "sonner";
import RichEditor from "../components/RichEditor";
import BiblePanel from "../components/BiblePanel";

const AI_LANGS = [
  { code: "en", label: "Inglés" },
  { code: "pt", label: "Portugués" },
  { code: "gn", label: "Guaraní" },
  { code: "de", label: "Alemán" },
  { code: "fr", label: "Francés" },
  { code: "ko", label: "Coreano" },
];

export default function StudyEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState("");
  const [uploading, setUploading] = useState(false);
  const [showShareHelper, setShowShareHelper] = useState(false);
  const dirty = useRef(false);
  const saveTimer = useRef(null);

  const load = async () => {
    const r = await api.get(`/admin/sermons/${id}`);
    setS(r.data);
  };
  useEffect(() => { load(); }, [id]);

  const scheduleSave = (patch) => {
    dirty.current = true;
    setS((cur) => ({ ...cur, ...patch }));
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => doSave(patch, true), 900);
  };

  const doSave = async (patchOverride = null, auto = false) => {
    if (!s) return;
    setSaving(true);
    try {
      const payload = patchOverride ? { ...s, ...patchOverride } : s;
      const r = await api.put(`/admin/sermons/${id}`, payload);
      setS(r.data);
      setSavedAt(new Date());
      dirty.current = false;
      if (!auto) toast.success("Guardado");
    } catch (e) {
      toast.error("No se pudo guardar");
    } finally { setSaving(false); }
  };

  const addSchedule = () => scheduleSave({ schedules: [...(s.schedules || []), { date: "", time: "", label: "" }] });
  const updSchedule = (i, patch) => {
    const next = [...(s.schedules || [])];
    next[i] = { ...next[i], ...patch };
    scheduleSave({ schedules: next });
  };
  const delSchedule = (i) => {
    const next = [...(s.schedules || [])];
    next.splice(i, 1);
    scheduleSave({ schedules: next });
  };

  const insertVerse = async () => {
    const ref = window.prompt("¿Qué pasaje querés insertar? (ej: Juan 3:16)");
    if (!ref) return;
    try {
      const r = await api.get("/bible/lookup", { params: { reference: ref, version: s.bible_version || "RV1909" } });
      const html = `<blockquote><strong>${r.data.reference}</strong> (${r.data.version}) — ${r.data.text}</blockquote>`;
      const next = (s.body_html || "") + html;
      scheduleSave({ body_html: next });
    } catch (e) { toast.error("No se encontró el versículo"); }
  };

  const insertFromPanel = ({ reference, version, text }) => {
    const html = `<blockquote><strong>${reference}</strong> (${version}) — ${text}</blockquote>`;
    scheduleSave({ body_html: (s.body_html || "") + html });
    toast.success("Versículo insertado");
  };

  const stripHtml = (html) => (html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

  const runAi = async (kind, language = null) => {
    const text = stripHtml(s.body_html);
    if (!text) { toast.error("Escribí la prédica primero"); return; }
    setAiBusy(kind + (language || ""));
    try {
      const r = await api.post("/admin/ai/generate", { kind, text, language });
      const result = r.data.result;
      if (kind === "summary") scheduleSave({ summary: result });
      else if (kind === "key_points") {
        const points = result.split("\n").map((l) => l.replace(/^[-•\d.\s)]+/, "").trim()).filter(Boolean);
        scheduleSave({ key_points: points });
      } else if (kind === "memory_verse") scheduleSave({ memory_verse: result });
      else if (kind === "questions") {
        const qs = result.split("\n").map((l) => l.replace(/^[\d.\s)]+/, "").trim()).filter(Boolean);
        scheduleSave({ group_questions: qs });
      } else if (kind === "translation") {
        const translations = { ...(s.translations || {}) };
        translations[language] = {
          ...(translations[language] || {}),
          body: result,
          title: s.title,
          summary: s.summary,
          key_points: s.key_points,
          memory_verse: s.memory_verse,
          group_questions: s.group_questions,
        };
        scheduleSave({ translations });
      }
      toast.success("Listo. Revisá antes de publicar.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error de IA");
    } finally { setAiBusy(""); }
  };

  const uploadFile = async (file, field) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      scheduleSave({ [field]: r.data.url });
      toast.success(`${field === "audio_url" ? "Audio" : "Video"} subido`);
    } catch (e) {
      toast.error("No se pudo subir el archivo");
    } finally { setUploading(false); }
  };

  const togglePublish = async () => {
    try {
      await doSave();
      if (s.status === "published") {
        await api.post(`/admin/sermons/${id}/unpublish`);
        toast.success("Movida a borrador");
        load();
      } else {
        const r = await api.post(`/admin/sermons/${id}/publish`);
        const notify = r.data?.notify_result;
        if (notify && notify.sent) {
          toast.success(`¡Publicada! Correo enviado a ${notify.sent} suscriptor${notify.sent === 1 ? "" : "es"}.`);
        } else {
          toast.success("¡Prédica publicada!");
        }
        setShowShareHelper(true);
        load();
      }
    } catch (e) { toast.error("No se pudo actualizar"); }
  };

  const copyWhatsappMessage = async () => {
    try {
      const r = await api.get("/settings/public");
      const base = (r.data?.site_public_url || window.location.origin).replace(/\/$/, "");
      const url = `${base}/predicas/${id}`;
      const parts = [`📖 ${s.title}`];
      if (s.bible_reference) parts.push(s.bible_reference);
      parts.push("");
      parts.push(url);
      const message = parts.join("\n");
      await navigator.clipboard.writeText(message);
      toast.success("Mensaje copiado. Pegalo en tu canal de WhatsApp.");
    } catch {
      toast.error("No pudimos copiar el mensaje.");
    }
  };

  if (!s) return <div className="p-10 text-center text-[#52635C]">Cargando…</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-8" data-testid="editor-page">
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <Link to="/estudio" className="btn-ghost !min-h-10" data-testid="btn-back-study"><ArrowLeft size={16} /> Volver</Link>
        <div className="text-sm text-[#52635C] font-mono">
          {saving ? <span className="flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Guardando…</span>
            : savedAt ? `Guardado · ${savedAt.toLocaleTimeString("es-PY", { hour: "2-digit", minute: "2-digit" })}` : "Sin cambios aún"}
        </div>
        <div className="ml-auto flex gap-2 flex-wrap">
          <Link to={`/pulpito/${id}`} className="btn-ghost" data-testid="btn-editor-pulpit"><Presentation size={16} /> Modo púlpito</Link>
          <a href={`${API_BASE}/sermons/${id}/pdf`} target="_blank" rel="noreferrer" className="btn-ghost" data-testid="btn-editor-pdf"><FileText size={16} /> PDF</a>
          <a href={`${API_BASE}/sermons/${id}/docx`} target="_blank" rel="noreferrer" className="btn-ghost" data-testid="btn-editor-docx"><Download size={16} /> Word</a>
          <button onClick={() => doSave()} className="btn-ghost" data-testid="btn-save"><Save size={16} /> Guardar</button>
          <button onClick={togglePublish} className="btn-gold" data-testid="btn-publish">
            <Globe2 size={16} /> {s.status === "published" ? "Despublicar" : "Publicar"}
          </button>
          {s.status === "published" && (
            <button onClick={copyWhatsappMessage} className="btn-ghost" data-testid="btn-copy-whatsapp">
              <MessageCircle size={16} /> Copiar mensaje para WhatsApp
            </button>
          )}
        </div>
      </div>

      {showShareHelper && (
        <div className="card-warm p-5 mb-5 border-2 border-[#E2B85C] relative" data-testid="share-helper">
          <button onClick={() => setShowShareHelper(false)} className="absolute top-3 right-3 text-[#52635C] hover:text-[#13302A]" aria-label="Cerrar"><X size={18} /></button>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-[#1E5244] text-white flex items-center justify-center shrink-0"><MessageCircle size={20} /></div>
            <div className="flex-1">
              <h3 className="font-serif text-lg font-semibold text-[#13302A] mb-1">¡Publicada! Avisá a la congregación</h3>
              <p className="text-sm text-[#52635C] mb-3">Los suscriptores por correo ya recibieron el aviso. Si querés, copiá el mensaje listo y pegalo en tu canal de WhatsApp con un toque.</p>
              <button onClick={copyWhatsappMessage} className="btn-gold" data-testid="btn-copy-whatsapp-helper">
                <MessageCircle size={16} /> Copiar mensaje para WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-[1fr_380px] gap-6">
        <div className="space-y-5">
          {/* Ficha */}
          <section className="card-warm p-5 space-y-4">
            <input
              value={s.title}
              onChange={(e) => scheduleSave({ title: e.target.value })}
              placeholder="Título de la prédica"
              className="input-warm !text-2xl font-serif !min-h-14"
              data-testid="input-title"
            />
            <div className="grid md:grid-cols-3 gap-3">
              <input
                value={s.series || ""}
                onChange={(e) => scheduleSave({ series: e.target.value })}
                placeholder="Serie (ej: Caminar en gracia)"
                className="input-warm"
                data-testid="input-series"
              />
              <input
                value={s.bible_reference || ""}
                onChange={(e) => scheduleSave({ bible_reference: e.target.value })}
                placeholder="Pasaje (ej: Juan 3:16)"
                className="input-warm"
                data-testid="input-reference"
              />
              <select
                value={s.bible_version || "NBLA"}
                onChange={(e) => scheduleSave({ bible_version: e.target.value })}
                className="input-warm"
                data-testid="select-version"
              >
                {["NBLA", "NTV", "NVI", "NVIC", "RVR09", "VBL", "PDDPT", "SIMPLE", "RV1909"].map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="eyebrow">Horarios y cultos</div>
                <button onClick={addSchedule} className="btn-ghost !min-h-8 !px-2 text-xs" data-testid="btn-add-schedule"><Plus size={14} /> Agregar</button>
              </div>
              <div className="space-y-2">
                {(s.schedules || []).map((sc, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1fr_2fr_auto] gap-2 items-center">
                    <input type="date" value={sc.date || ""} onChange={(e) => updSchedule(i, { date: e.target.value })} className="input-warm" data-testid={`sched-date-${i}`} />
                    <input type="time" value={sc.time || ""} onChange={(e) => updSchedule(i, { time: e.target.value })} className="input-warm" data-testid={`sched-time-${i}`} />
                    <input value={sc.label || ""} onChange={(e) => updSchedule(i, { label: e.target.value })} placeholder="Etiqueta (Culto domingo)" className="input-warm" data-testid={`sched-label-${i}`} />
                    <button onClick={() => delSchedule(i)} className="btn-ghost !min-h-10" data-testid={`sched-del-${i}`}><Trash2 size={14} /></button>
                  </div>
                ))}
                {(s.schedules || []).length === 0 && <div className="text-sm text-[#52635C] italic">Sin horarios cargados.</div>}
              </div>
            </div>
          </section>

          {/* Editor */}
          <section className="card-warm p-5 space-y-3">
            <div className="eyebrow">Prédica</div>
            <RichEditor
              value={s.body_html || ""}
              onChange={(html) => scheduleSave({ body_html: html })}
              onInsertVerse={insertVerse}
            />
          </section>

          {/* AI Toolkit */}
          <section className="card-warm p-5 space-y-4">
            <div className="flex items-center gap-2">
              <Sparkles size={16} className="text-[#E2B85C]" />
              <h3 className="font-serif text-lg font-semibold text-[#13302A]">Asistente pastoral (IA)</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => runAi("summary")} disabled={!!aiBusy} className="btn-ghost" data-testid="ai-summary">
                {aiBusy === "summary" ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Resumen
              </button>
              <button onClick={() => runAi("key_points")} disabled={!!aiBusy} className="btn-ghost" data-testid="ai-key-points">
                {aiBusy === "key_points" ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Puntos clave
              </button>
              <button onClick={() => runAi("memory_verse")} disabled={!!aiBusy} className="btn-ghost" data-testid="ai-memory-verse">
                {aiBusy === "memory_verse" ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Versículo para memorizar
              </button>
              <button onClick={() => runAi("questions")} disabled={!!aiBusy} className="btn-ghost" data-testid="ai-questions">
                {aiBusy === "questions" ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Preguntas grupo pequeño
              </button>
            </div>

            <div>
              <div className="eyebrow mb-2">Resumen</div>
              <textarea value={s.summary || ""} onChange={(e) => scheduleSave({ summary: e.target.value })} className="input-warm min-h-[90px]" placeholder="Breve resumen pastoral…" data-testid="input-summary" />
            </div>
            <div>
              <div className="eyebrow mb-2">Puntos clave</div>
              <textarea
                value={(s.key_points || []).join("\n")}
                onChange={(e) => scheduleSave({ key_points: e.target.value.split("\n").map((l) => l.trim()).filter(Boolean) })}
                className="input-warm min-h-[90px]"
                placeholder="Uno por línea"
                data-testid="input-key-points"
              />
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <div className="eyebrow mb-2">Versículo para memorizar</div>
                <input value={s.memory_verse || ""} onChange={(e) => scheduleSave({ memory_verse: e.target.value })} className="input-warm" data-testid="input-memory-verse" />
              </div>
              <div>
                <div className="eyebrow mb-2">Esquema (modo púlpito)</div>
                <textarea
                  value={(s.outline || []).join("\n")}
                  onChange={(e) => scheduleSave({ outline: e.target.value.split("\n").map((l) => l.trim()).filter(Boolean) })}
                  className="input-warm min-h-[90px]"
                  placeholder="Introducción, Punto 1, Punto 2, Conclusión…"
                  data-testid="input-outline"
                />
              </div>
            </div>
            <div>
              <div className="eyebrow mb-2">Preguntas para grupos pequeños</div>
              <textarea
                value={(s.group_questions || []).join("\n")}
                onChange={(e) => scheduleSave({ group_questions: e.target.value.split("\n").map((l) => l.trim()).filter(Boolean) })}
                className="input-warm min-h-[100px]"
                placeholder="Una pregunta por línea"
                data-testid="input-group-questions"
              />
            </div>
          </section>

          {/* Media + translations */}
          <section className="card-warm p-5 space-y-4">
            <div className="flex items-center gap-2">
              <Upload size={16} className="text-[#1F4A40]" />
              <h3 className="font-serif text-lg font-semibold text-[#13302A]">Audio y video del culto</h3>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className="eyebrow mb-2">Audio (MP3)</div>
                <input type="file" accept="audio/*" onChange={(e) => uploadFile(e.target.files[0], "audio_url")} className="text-sm" data-testid="file-audio" />
                {s.audio_url && <div className="text-xs text-[#52635C] mt-1 truncate">{s.audio_url}</div>}
              </div>
              <div>
                <div className="eyebrow mb-2">Video</div>
                <input type="file" accept="video/*" onChange={(e) => uploadFile(e.target.files[0], "video_url")} className="text-sm" data-testid="file-video" />
                {s.video_url && <div className="text-xs text-[#52635C] mt-1 truncate">{s.video_url}</div>}
              </div>
            </div>
            {uploading && <div className="text-sm text-[#52635C]"><Loader2 size={14} className="inline animate-spin" /> Subiendo…</div>}
            <p className="text-xs text-[#8B9995]">La transcripción automática del audio estará disponible en la próxima iteración (texto + subtítulos). Por ahora podés subir audio y video, y usar la IA sobre el texto que escribas.</p>
          </section>

          <section className="card-warm p-5 space-y-3">
            <h3 className="font-serif text-lg font-semibold text-[#13302A]">Traducciones (texto + PDF + subtítulos)</h3>
            <p className="text-sm text-[#52635C]">La IA traduce fielmente desde el texto principal. Revisá antes de publicar.</p>
            <div className="flex flex-wrap gap-2">
              {AI_LANGS.map((l) => (
                <button key={l.code} onClick={() => runAi("translation", l.code)} disabled={!!aiBusy} className={`btn-ghost ${s.translations?.[l.code] ? "border-[#E2B85C]" : ""}`} data-testid={`ai-tr-${l.code}`}>
                  {aiBusy === "translation" + l.code ? <Loader2 size={14} className="animate-spin" /> : <Globe2 size={14} />} {l.label}
                  {s.translations?.[l.code] && <span className="badge-gold ml-1">OK</span>}
                </button>
              ))}
            </div>
          </section>
        </div>

        <div className="lg:sticky lg:top-24 self-start min-h-0 h-[80vh]">
          <BiblePanel onInsert={insertFromPanel} />
        </div>
      </div>
    </div>
  );
}
